// frontend/src/components/Diario/ModalPesagem.jsx
// Modal completo para registro de pesagem

import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { Scale, Save, X, ChevronDown } from 'lucide-react';
import Modal from '../UI/Modal';
import DropdownMenu from '../UI/DropdownMenu.jsx';

const TURNOS = ['Manhã', 'Tarde', 'Noite', 'Madrugada'];

/**
 * Modal completo para registro de pesagem
 */
const ModalPesagem = ({ isOpen, onClose, pacienteId, onCreateRegistro, onRegistroSucesso }) => {
    const [formData, setFormData] = useState({
        peso: '',
        observacoes: '',
        turno: 'Manhã'
    });
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        
        if (errors[name]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.peso.trim()) {
            newErrors.peso = 'Peso é obrigatório';
        } else {
            const pesoNum = parseFloat(formData.peso.replace(',', '.'));
            if (isNaN(pesoNum) || pesoNum <= 0) {
                newErrors.peso = 'Peso deve ser um número válido maior que zero';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoading(true);

        try {
            const pesoNum = parseFloat(formData.peso.replace(',', '.'));
            
            const dataToSend = {
                paciente_id: pacienteId,
                tipo_registro: 'Pesagem',
                observacoes: formData.observacoes || null,
                turno: formData.turno,
                detalhes: {
                    peso: pesoNum,
                    unidade: 'kg'
                }
            };

            await onCreateRegistro(dataToSend);
            toast.success('Registro de pesagem salvo com sucesso!');
            
            if (onRegistroSucesso) {
                onRegistroSucesso();
            }
            
            // Resetar formulário
            setFormData({
                peso: '',
                observacoes: '',
                turno: 'Manhã'
            });
            setErrors({});
            onClose();
        } catch (error) {
            toast.error(error.message || 'Erro ao registrar pesagem');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading) {
            setFormData({
                peso: '',
                observacoes: '',
                turno: 'Manhã'
            });
            setErrors({});
            onClose();
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} title="Registrar Pesagem" size="lg">
            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Peso */}
                <div>
                    <label htmlFor="peso" className="block text-sm font-medium text-gray-700 mb-2">
                        <Scale size={16} className="inline mr-2" />
                        Peso (kg) <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        name="peso"
                        id="peso"
                        value={formData.peso}
                        onChange={handleChange}
                        placeholder="Ex: 70.5 ou 70,5"
                        className={`
                            mt-1 block w-full border rounded-md shadow-sm p-3 text-sm
                            focus:ring-emerald-500 focus:border-emerald-500
                            ${errors.peso ? 'border-red-300' : 'border-gray-300'}
                        `}
                    />
                    {errors.peso && (
                        <p className="mt-1 text-xs text-red-600">{errors.peso}</p>
                    )}
                </div>

                {/* Turno */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Turno <span className="text-red-500">*</span>
                    </label>
                    <DropdownMenu
                        trigger={
                            <button
                                type="button"
                                className="mt-1 w-full flex items-center justify-between border border-gray-300 rounded-md shadow-sm p-3 text-sm focus:ring-emerald-500 focus:border-emerald-500 text-gray-900"
                            >
                                <span>{formData.turno}</span>
                                <ChevronDown size={18} className="text-gray-400" />
                            </button>
                        }
                        items={TURNOS.map(turno => ({
                            id: turno,
                            label: turno,
                            onClick: () => {
                                setFormData(prev => ({ ...prev, turno }));
                            }
                        }))}
                        position="bottom-left"
                        className="w-full"
                    />
                </div>

                {/* Observações */}
                <div>
                    <label htmlFor="observacoes" className="block text-sm font-medium text-gray-700 mb-2">
                        Observações (Opcional)
                    </label>
                    <textarea
                        name="observacoes"
                        id="observacoes"
                        value={formData.observacoes}
                        onChange={handleChange}
                        placeholder="Adicione observações sobre a pesagem..."
                        rows="4"
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-3 text-sm focus:ring-emerald-500 focus:border-emerald-500"
                    />
                </div>

                {/* Botões */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                    <button
                        type="button"
                        onClick={handleClose}
                        disabled={loading}
                        className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 transition disabled:opacity-50"
                    >
                        <X size={18} />
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        disabled={loading}
                        className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-md transition disabled:opacity-50"
                    >
                        {loading ? (
                            <>Salvando...</>
                        ) : (
                            <>
                                <Save size={18} />
                                Registrar Pesagem
                            </>
                        )}
                    </button>
                </div>
            </form>
        </Modal>
    );
};

export default ModalPesagem;

